import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Calendar } from '../components/ui/calendar';
import { CheckCircle, Users, Zap, Clock, Mic, ArrowLeft, Loader2, Building2, Star } from 'lucide-react';
import { formatDateUK, formatDateISO, formatCurrencySimple, parseISODate } from '../utils/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const SystemDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const businessId = searchParams.get('business');
  const [selectedDate, setSelectedDate] = useState(null);
  const [system, setSystem] = useState(null);
  const [bookedDates, setBookedDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSystem();
    fetchAvailability();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchSystem = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/systems/${id}`);
      setSystem(response.data);
    } catch (err) {
      console.error('Failed to fetch system:', err);
      setError('Failed to load PA system details');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailability = async () => {
    try {
      const today = new Date();
      const sixMonthsLater = new Date();
      sixMonthsLater.setMonth(today.getMonth() + 6);
      
      const response = await axios.get(
        `${API}/bookings/availability/${id}?start_date=${formatDateISO(today)}&end_date=${formatDateISO(sixMonthsLater)}`
      );
      
      const bookedDateObjects = (response.data?.booked_dates || []).map(dateStr => parseISODate(dateStr));
      setBookedDates(bookedDateObjects);
    } catch (err) {
      console.error('Failed to fetch availability:', err);
    }
  };

  const isDateBooked = (date) => {
    return bookedDates.some(bookedDate => 
      bookedDate.getDate() === date.getDate() &&
      bookedDate.getMonth() === date.getMonth() &&
      bookedDate.getFullYear() === date.getFullYear()
    );
  };

  const handleBookNow = () => {
    const bookingUrl = `/booking/${system?.system_id}${businessId ? `?business=${businessId}` : ''}`;
    if (selectedDate) {
      navigate(bookingUrl, { state: { selectedDate } });
    } else {
      navigate(bookingUrl);
    }
  };

  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${BACKEND_URL}${url}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-yellow-500 mx-auto mb-4" />
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (error || !system) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">PA System Not Found</h2>
          <Button onClick={() => navigate('/browse')} className="bg-yellow-500 hover:bg-yellow-600 text-black">
            Back to Browse
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Button
          variant="ghost"
          className="mb-6 text-gray-400 hover:text-white hover:bg-gray-800"
          onClick={() => navigate('/browse')}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Browse
        </Button>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left Column - Image and Details */}
          <div className="space-y-6">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src={getImageUrl(system?.image)}
                alt={system?.name}
                className="w-full h-96 object-cover"
              />
              <div className="absolute top-4 right-4">
                <Badge className="bg-yellow-500 text-black text-sm py-1 px-3 font-semibold">
                  {system?.category}
                </Badge>
              </div>
              {system?.available && (
                <div className="absolute top-4 left-4">
                  <Badge className="bg-green-500 text-white text-sm py-1 px-3">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Available
                  </Badge>
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-transparent to-transparent" />
            </div>

            {/* Business Info */}
            {system?.business && (
              <Card 
                className="bg-gray-800 border-gray-700 cursor-pointer hover:border-yellow-500 transition-colors"
                onClick={() => navigate(`/business/${system.business.business_id}`)}
              >
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <Building2 className="h-10 w-10 text-yellow-500" />
                    <div className="flex-1">
                      <p className="text-gray-400 text-sm">Provided by</p>
                      <h3 className="text-xl font-semibold text-white">{system.business.business_name}</h3>
                      {system.business.average_rating && (
                        <div className="flex items-center gap-1 mt-1">
                          <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                          <span className="text-gray-400">{system.business.average_rating} rating</span>
                        </div>
                      )}
                    </div>
                    <Badge className="bg-green-600 text-white">Verified</Badge>
                  </div>
                </CardContent>
              </Card>
            )}

            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white">Specifications</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-4">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-yellow-500/20 rounded-lg">
                    <Zap className="h-5 w-5 text-yellow-500" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Power</p>
                    <p className="font-semibold text-white">{system?.specs?.power || 'N/A'}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-yellow-500/20 rounded-lg">
                    <Users className="h-5 w-5 text-yellow-500" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Coverage</p>
                    <p className="font-semibold text-white">{system?.specs?.coverage || 'N/A'}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-yellow-500/20 rounded-lg">
                    <Clock className="h-5 w-5 text-yellow-500" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Setup Time</p>
                    <p className="font-semibold text-white">{system?.specs?.setup || 'N/A'}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-yellow-500/20 rounded-lg">
                    <Mic className="h-5 w-5 text-yellow-500" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Microphones</p>
                    <p className="font-semibold text-white">{system?.specs?.microphones || 'N/A'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Info and Booking */}
          <div className="space-y-6">
            <div>
              <h1 className="text-4xl font-bold text-white mb-4">{system?.name}</h1>
              <p className="text-xl text-gray-400 mb-6">{system?.description}</p>
              <div className="flex items-center space-x-2 mb-6">
                <Users className="h-5 w-5 text-yellow-500" />
                <span className="text-lg text-gray-300">Perfect for: {system?.capacity}</span>
              </div>
              <div className="flex items-baseline mb-8">
                <span className="text-5xl font-bold text-yellow-500">{formatCurrencySimple(system?.price || 0)}</span>
                <span className="text-xl text-gray-500 ml-3">{system?.price_unit || 'per day'}</span>
              </div>
            </div>

            <Separator className="bg-gray-700" />

            <div>
              <h3 className="text-2xl font-bold text-white mb-4">Features Included</h3>
              <div className="grid gap-3">
                {(system?.features || []).map((feature, idx) => (
                  <div key={idx} className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    <span className="text-gray-300">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            <Separator className="bg-gray-700" />

            <div>
              <h3 className="text-2xl font-bold text-white mb-4">Check Availability</h3>
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="pt-6">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    disabled={(date) => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      return date < today || isDateBooked(date);
                    }}
                    modifiers={{
                      booked: bookedDates
                    }}
                    modifiersStyles={{
                      booked: { backgroundColor: '#ef4444', color: 'white', opacity: 0.7 }
                    }}
                    className="rounded-md w-full text-white"
                  />
                  {selectedDate && (
                    <p className="mt-4 text-center text-green-400">
                      Selected: {formatDateUK(selectedDate)}
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>

            <Button
              size="lg"
              className="w-full py-6 text-lg bg-yellow-500 hover:bg-yellow-600 text-black font-bold"
              onClick={handleBookNow}
              disabled={!system?.available}
            >
              {system?.available ? 'Book This System' : 'Currently Unavailable'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemDetail;
